import { useMemo, useState } from 'react';
import BlockCard from './BlockCard';
import HelpPopover from './HelpPopover';
import { regionDisplayName } from './utils/formatRegion';
import styles from './blocks.module.css';

function blockMatchesQuery(block, query) {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const name = String(block?.name || '').toLowerCase();
    if (name.includes(q)) return true;
    const regions = Array.isArray(block?.regionDefinitions) ? block.regionDefinitions : [];
    return regions.some((r) => regionDisplayName(r).toLowerCase().includes(q));
}

/**
 * Scannable block map. Dense rows + filter for large case maps.
 */
export default function BlockMap({
    blocks = [],
    showUsage = true,
    onUsageClick,
    onEditBlock,
    onAddBlock,
    onLoadProtocol,
    loadPending = false,
    title = 'Configure Blocks',
}) {
    const list = Array.isArray(blocks) ? blocks : [];
    const [filter, setFilter] = useState('');

    const filtered = useMemo(
        () => list.filter((b) => blockMatchesQuery(b, filter)),
        [list, filter]
    );

    const showFilter = list.length >= 8;
    const empty = list.length === 0;
    const loadIsPrimary = empty && onLoadProtocol;

    return (
        <div className={styles.root}>
            <div className={styles.mapHeader}>
                <div className={styles.mapHeaderLeft}>
                    <h3 className={styles.mapTitle}>{title}</h3>
                    <HelpPopover label="How blocks work" title="How blocks work">
                        <p>
                            A protocol is a starting set of tissue blocks your lab uses often.
                            Load one to fill this list, then treat it as this case’s map — not a
                            locked template.
                        </p>
                        <p>
                            You can add extra blocks, leave some unused, or change what is in a
                            block (which regions, and details like hemisphere or landmarks). Any
                            edits you make are specific to this case, so you don’t need to worry
                            about accidentally messing up the protocol.
                        </p>
                        <p>Click a block to open its editor.</p>
                    </HelpPopover>
                    <span className={styles.mapCount}>
                        {filter.trim()
                            ? `${filtered.length} of ${list.length}`
                            : `${list.length} block${list.length === 1 ? '' : 's'}`}
                    </span>
                </div>
                <div className={styles.mapHeaderActions}>
                    {showFilter && (
                        <input
                            type="search"
                            className={styles.filterInput}
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Filter blocks or regions…"
                            aria-label="Filter blocks or regions"
                        />
                    )}
                    {onLoadProtocol && (
                        <button
                            type="button"
                            className={loadIsPrimary ? styles.buttonPrimary : styles.buttonSecondary}
                            onClick={onLoadProtocol}
                            disabled={loadPending}
                        >
                            {loadPending ? 'Loading…' : 'Load protocol'}
                        </button>
                    )}
                    {onAddBlock && (
                        <button
                            type="button"
                            className={loadIsPrimary ? styles.buttonSecondary : styles.buttonPrimary}
                            onClick={onAddBlock}
                        >
                            + Add block
                        </button>
                    )}
                </div>
            </div>

            {empty ? (
                <div className={styles.emptyState}>
                    <p className={styles.emptyTitle}>No blocks yet</p>
                    <p className={styles.jobText}>
                        Load a protocol to start, or add a block by hand.
                    </p>
                </div>
            ) : filtered.length === 0 ? (
                <p className={styles.jobText}>No blocks match “{filter.trim()}”.</p>
            ) : (
                <div className={styles.rowList} role="list">
                    {filtered.map((block) => (
                        <div key={block._id} role="listitem">
                            <BlockCard
                                block={block}
                                showUsage={showUsage}
                                onUsageClick={onUsageClick}
                                onEditBlock={onEditBlock}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
